import React, { useContext, useEffect, useMemo, useState } from 'react';
import { BiCheck, BiX, BiErrorCircle } from 'react-icons/bi';
import apiUser from '../../../../apiUser';
import { UserContext } from '../../../../contexts/userProvider';
import { brl, hojeBrasilia, resumoPrimeiroFaturamento } from './eventosTimeline';

// Ativar contrato = evento "Homologação da Instalação" (tipo homologacao, modo
// instalacao). modo 'corrigir': mesma tela para trocar a data antes do primeiro
// faturamento (o backend valida a janela). `inicial` (opcional) pré-preenche o
// responsável no cliente e mostra a data atual na correção.

const inputCls = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2.5 text-gray-900 dark:text-gray-100 focus:border-vox-blue-500 focus:ring-2 focus:ring-vox-blue-500';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5';
export const botaoAtivarCls = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-vox-green-500 font-bold text-vox-blue-950 shadow-sm transition-colors duration-150 hover:bg-vox-green-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vox-green-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-vox-green-500';

const PrimeiroFaturamento = ({ temData, carregando, erro, resumo }) => (
    <section aria-live='polite' className='rounded-xl border border-sky-200 dark:border-sky-900 bg-sky-50/70 dark:bg-sky-950/30 p-4'>
        <div className='flex items-baseline justify-between gap-3'>
            <h3 className='text-sm font-semibold text-sky-900 dark:text-sky-100'>Primeiro faturamento</h3>
            {carregando && <span className='text-xs text-sky-700/80 dark:text-sky-300/80'>calculando…</span>}
        </div>

        {!temData ? (
            <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>Informe a data da ativação para ver o valor proporcional.</p>
        ) : erro ? (
            <p className='mt-1 text-sm text-amber-800 dark:text-amber-300'>{erro}</p>
        ) : resumo ? (
            <>
                {/* Ciclo do primeiro faturamento: a parte cheia é o que será cobrado. */}
                <div className='mt-4' aria-hidden='true'>
                    <div className='relative h-4'>
                        <span
                            className='absolute -translate-x-1/2 whitespace-nowrap text-xs font-semibold text-sky-800 dark:text-sky-200 tabular-nums'
                            style={{ left: `${Math.min(88, Math.max(12, (1 - resumo.fracao) * 100))}%` }}>
                            ativação {resumo.ativacao}
                        </span>
                    </div>
                    <div className='relative mt-1 h-2.5 overflow-hidden rounded-full bg-sky-100 dark:bg-sky-900/60'>
                        <div className='absolute inset-y-0 right-0 rounded-full bg-sky-500 motion-safe:transition-[width] motion-safe:duration-300'
                            style={{ width: `${resumo.fracao * 100}%` }} />
                    </div>
                    <div className='mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400 tabular-nums'>
                        <span>{resumo.inicio}</span>
                        <span>{resumo.fim}</span>
                    </div>
                </div>

                <dl className='mt-3 text-sm'>
                    <div className='flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5'>
                        <dt className='text-gray-700 dark:text-gray-300'>
                            <span className='font-semibold text-gray-900 dark:text-white'>{resumo.competencia}</span>
                            <span className='tabular-nums'>: {resumo.diasCobrados} de {resumo.diasCiclo} dias × {brl(resumo.mensal)}</span>
                        </dt>
                        <dd className='text-lg font-bold text-gray-900 dark:text-white tabular-nums'>{brl(resumo.proporcional)}</dd>
                    </div>
                    <div className='mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 border-t border-dashed border-sky-200 dark:border-sky-900 pt-2'>
                        <dt className='text-gray-600 dark:text-gray-400'>a partir de {resumo.seguinte}</dt>
                        <dd className='text-gray-900 dark:text-gray-100 tabular-nums'>
                            <span className='font-semibold'>{brl(resumo.mensal)}</span> por mês
                        </dd>
                    </div>
                </dl>
                {resumo.mensal === 0 && (
                    <p className='mt-3 text-xs text-amber-800 dark:text-amber-300'>
                        O valor mensal está zerado: confira o Valor Global ou os itens do contrato antes de ativar.
                    </p>
                )}
            </>
        ) : null}
    </section>
);

const AtivarContrato = ({ contrato, client, modo = 'ativar', inicial, onClose, onAtivado }) => {
    const { user } = useContext(UserContext) || {};
    const corrigir = modo === 'corrigir';
    const hoje = useMemo(() => hojeBrasilia(), []);
    const [data, setData] = useState('');
    const [responsavel, setResponsavel] = useState((inicial && inicial.responsavel_cliente) || '');
    const [observacao, setObservacao] = useState('');
    const [previa, setPrevia] = useState(null);
    const [erroPrevia, setErroPrevia] = useState('');
    const [carregandoPrevia, setCarregandoPrevia] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState('');

    // Prévia ao vivo do primeiro faturamento (debounce 400ms) quando há data.
    useEffect(() => {
        setPrevia(null);
        setErroPrevia('');
        if (!data) { setCarregandoPrevia(false); return undefined; }
        let vivo = true;
        setCarregandoPrevia(true);
        const t = setTimeout(() => {
            apiUser.post('/contratos/eventos/preview', {
                id_contrato: contrato.id, tipo: 'homologacao', modo: 'instalacao',
                data_evento: `${data} 00:00:00`, responsavel_cliente: responsavel.trim() || undefined,
            })
                .then(({ data: r }) => {
                    if (!vivo) return;
                    if (r && r.success) setPrevia(r.dados);
                    else setErroPrevia((r && r.message) || 'Não foi possível calcular a prévia.');
                })
                .catch(() => vivo && setErroPrevia('Não foi possível calcular a prévia agora.'))
                .finally(() => vivo && setCarregandoPrevia(false));
        }, 400);
        return () => { vivo = false; clearTimeout(t); };
        // A prévia só depende da data; o responsável vai junto apenas por completude.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, contrato.id]);

    // Com algo digitado (data, responsável diferente do pré-preenchido ou observação),
    // Esc e clique fora não fecham: só os botões Cancelar/Fechar.
    const responsavelInicial = ((inicial && inicial.responsavel_cliente) || '').trim();
    const temDados = Boolean(data) || responsavel.trim() !== responsavelInicial || Boolean(observacao.trim());
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !enviando && !temDados) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [enviando, temDados, onClose]);

    const resumo = resumoPrimeiroFaturamento(previa);
    const podeEnviar = Boolean(data) && Boolean(responsavel.trim()) && !enviando;

    const enviar = async (e) => {
        e.preventDefault();
        if (!podeEnviar) return;
        setEnviando(true);
        setErro('');
        try {
            const { data: r } = await apiUser.post('/contratos/eventos/lancar', {
                id_contrato: contrato.id, tipo: 'homologacao', modo: 'instalacao',
                data_evento: `${data} 00:00:00`, responsavel_cliente: responsavel.trim(), motivo: observacao.trim(),
            });
            if (r && r.success) {
                // A mensagem pode trazer o aviso de ciclo retroativo (mês já fechado).
                alert(r.message);
                onAtivado && onAtivado(r.dados);
            } else {
                setErro((r && r.message) || 'O contrato não foi ativado. Confira os campos e tente de novo.');
            }
        } catch (err) {
            setErro('Sem resposta do servidor. O contrato não foi ativado; tente de novo.');
        } finally {
            setEnviando(false);
        }
    };

    const titulo = corrigir ? 'Corrigir data da ativação' : 'Ativar contrato — Homologação da Instalação';
    const nomeVox = (user && user.nome) || 'Usuário logado';

    return (
        <div className='fixed inset-0 z-60 flex items-center justify-center p-4'>
            <div className='fixed inset-0 bg-black/50' onClick={() => { if (!enviando && !temDados) onClose(); }} />
            <form
                role='dialog' aria-modal='true' aria-labelledby='ativar-contrato-titulo'
                onSubmit={enviar}
                className='relative flex max-h-[92vh] w-full max-w-xl flex-col rounded-xl bg-white dark:bg-gray-800 shadow-2xl'>
                <header className='flex items-start justify-between gap-4 border-b border-gray-200 dark:border-gray-700 px-6 py-4'>
                    <div className='min-w-0'>
                        <h2 id='ativar-contrato-titulo' className='text-lg font-bold text-vox-blue-900 dark:text-white'>{titulo}</h2>
                        <p className='mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400'>
                            {contrato.nome}{client && client.nome ? ` · ${client.nome}` : ''}
                        </p>
                    </div>
                    <button type='button' onClick={onClose} disabled={enviando} aria-label='Fechar'
                        className='-mr-2 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vox-blue-500'>
                        <BiX className='h-6 w-6' />
                    </button>
                </header>

                <div className='flex-1 min-h-0 space-y-5 overflow-auto px-6 py-5'>
                    <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                        <div>
                            <label htmlFor='ativar-data' className={labelCls}>Data da ativação <span className='text-red-600'>*</span></label>
                            <input id='ativar-data' type='date' required autoFocus max={hoje} value={data}
                                onChange={(e) => setData(e.target.value)} className={inputCls} />
                            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                                {corrigir && inicial && inicial.data ? `Data atual: ${inicial.data}. ` : ''}Base do cálculo proporcional.
                            </p>
                        </div>
                        <div>
                            <label htmlFor='ativar-responsavel' className={labelCls}>Responsável no cliente <span className='text-red-600'>*</span></label>
                            <input id='ativar-responsavel' type='text' required maxLength={150} value={responsavel}
                                onChange={(e) => setResponsavel(e.target.value)} className={inputCls}
                                placeholder='Quem aceitou a instalação' />
                        </div>
                    </div>

                    <div>
                        <p className={labelCls}>Responsável na Vox</p>
                        <p className='flex items-center gap-2.5 text-sm text-gray-900 dark:text-gray-100'>
                            <span aria-hidden='true' className='flex h-7 w-7 items-center justify-center rounded-full bg-vox-blue-100 text-xs font-bold text-vox-blue-800 dark:bg-vox-blue-800 dark:text-vox-blue-100'>
                                {nomeVox.trim().charAt(0).toUpperCase()}
                            </span>
                            <span><span className='font-medium'>{nomeVox}</span> <span className='text-gray-500 dark:text-gray-400'>(você)</span></span>
                        </p>
                    </div>

                    <div>
                        <label htmlFor='ativar-observacao' className={labelCls}>
                            Observação <span className='font-normal text-gray-500 dark:text-gray-400'>(opcional)</span>
                        </label>
                        <textarea id='ativar-observacao' rows={2} value={observacao}
                            onChange={(e) => setObservacao(e.target.value)} className={inputCls}
                            placeholder='Ex.: termo de aceite assinado pelo secretário' />
                    </div>

                    <PrimeiroFaturamento temData={Boolean(data)} carregando={carregandoPrevia} erro={erroPrevia} resumo={resumo} />

                    {erro && (
                        <p role='alert' className='flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'>
                            <BiErrorCircle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden='true' />
                            <span>{erro}</span>
                        </p>
                    )}
                </div>

                <footer className='flex flex-col-reverse gap-2 border-t border-gray-200 dark:border-gray-700 px-6 py-4 sm:flex-row sm:justify-end'>
                    <button type='button' onClick={onClose} disabled={enviando}
                        className='rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vox-blue-500'>
                        Cancelar
                    </button>
                    <button type='submit' disabled={!podeEnviar} className={`${botaoAtivarCls} px-5 py-2.5 text-sm`}>
                        <BiCheck className='h-5 w-5' aria-hidden='true' />
                        {enviando ? (corrigir ? 'Salvando…' : 'Ativando…') : (corrigir ? 'Salvar nova data' : 'Ativar contrato')}
                    </button>
                </footer>
            </form>
        </div>
    );
};

export default AtivarContrato;
